import { getEventBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { BookingCancellationDto } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/NewCancelBookingModal';
import { stringNotEmpty } from 'Util/TypeGuards';
import axios from 'axios';
import { BOOKING_ENTITY_URL, EVENT_BOOKING_URL, FERRY_BOOKING_URL } from 'Constants';
import { Location } from 'react-router-dom';
import useAsync from 'Hooks/useAsync';
import { BookingEntity, DiscountAlteration, FeeEntity, GiftCertificateUsageEntity } from 'Models/Entities';
import { buildUrl } from 'Util/FetchUtils';
import { locationType } from 'Models/Enums';
import {
	EventBookingWizardData,
	SelectedTicketsDto,
} from 'Views/Components/_HumanWritten/BookingWizard/EventsBookingWizard/EventsBookingWizardData';

export interface EventBookingCreationDto {
	bookingId?: string;
	userId: string;
	eventId?: string;
	primaryFirstName?: string;
	primaryLastName?: string;
	primaryPhone?: string;
	primaryEmail?: string;
	selectedTickets?: SelectedTicketsDto[];
	existingTransactionId?: string;
	location: locationType;
}

export interface EventBookingPricesDto {
	transactionId: string;
	subtotalPrice: number;
	gstPrice: number;
	surchargePrice: number;
	giftCertificateUsages: GiftCertificateUsageEntity[];
	discounts: DiscountAlteration[];
	totalPrice: number;
	serviceFees: FeeEntity[];
}

export function dataToEventBookingDto(data: EventBookingWizardData): EventBookingCreationDto {
	return {
		bookingId: stringNotEmpty(data.bookingToEditId) ? data.bookingToEditId : undefined,
		eventId: data.eventId,
		userId: data.userId ?? '',
		firstName: data.firstName,
		lastName: data.lastName,
		phone: data.phone,
		email: data.email,
		selectedTickets: data.selectedTickets,
		existingTransactionId: getEventBookingTransactionIdFromStorage() ?? null,
		location: 'BOOKING_WIZARD',
	} as EventBookingCreationDto;
}

// Create a new booking end-point
export async function createEventBooking(bookingInformation: EventBookingCreationDto) {
	return axios.post(`${EVENT_BOOKING_URL}/new-booking`, bookingInformation, {
		validateStatus: () => true,
	});
}

// Alter an existing booking end-point
export async function alterEventBooking(bookingInformation: EventBookingCreationDto) {
	return axios.post(`${EVENT_BOOKING_URL}/alter-booking`, bookingInformation, {
		validateStatus: () => true,
	});
}

export async function GetPrices(transactionId: string): Promise<EventBookingPricesDto> {
	const response = await axios.get(buildUrl(`${EVENT_BOOKING_URL}/calculate-price`, { transactionId }));
	return response.data as EventBookingPricesDto;
}

/**
 * Returns true if user can navigate straight to the cart. Otherwise, return false.
 *
 * At the moment this is only valid when a user has an existing reserved booking in local storage.
 */
export function canNavigateToEventsCartStep(location: Location): boolean {
	const { pathname } = location;
	const existingTransaction = getEventBookingTransactionIdFromStorage();

	return (
		existingTransaction !== null && (pathname === '/event-booking-wizard/cart' || pathname.includes('/t-and-cs'))
	);
}

/**
 * Returns true when user can navigate to the payment step. Otherwise, return false.
 *
 * This will allow a user to have an incomplete booking in the wizard and navigate to the payment step if there is at
 * least one booking in the cart.
 */
export function canNavigateToEventsPaymentStep(location: Location): boolean {
	const { pathname } = location;
	const existingTransaction = getEventBookingTransactionIdFromStorage();

	return existingTransaction !== null && pathname === '/event-booking-wizard/payment';
}

/**
 * Returns true when user can navigate to the post payment step. Otherwise, return false.
 *
 * This will allow a user to have an incomplete booking in the wizard and navigate to the post payment step if there is
 * at least one booking in the cart.
 */
export function canNavigateToEventsPostPaymentStep(location: Location): boolean {
	const { pathname } = location;
	const existingTransaction = getEventBookingTransactionIdFromStorage();

	return existingTransaction !== null && pathname === '/event-booking-wizard/post-payment';
}

export function FetchEventBookingsForCartAsync(transactionId: string | null, userId?: string | null) {
	return useAsync(() => FetchEventBookingsForCart(transactionId), []);
}

/**
 * Fetches event bookings to be displayed on the cart tab of the booking wizard.
 *
 * @param transactionId
 */
export async function FetchEventBookingsForCart(transactionId: string | null): Promise<BookingEntity | null> {
	if (transactionId === null || transactionId === '') {
		return null;
	}
	try {
		const result = await axios.get(buildUrl(`${EVENT_BOOKING_URL}/fetch-bookings-for-cart`, { transactionId }));
		return new BookingEntity(result.data);
	} catch (e) {
		console.error(e);
		return null;
	}
}

export async function FetchEventBookingForBookingDetailedView(
	bookingId: string,
	userId: string | null,
): Promise<BookingEntity> {
	const results = await axios.get(
		buildUrl(`${BOOKING_ENTITY_URL}/fetch-booked-booking-by-id`, {
			bookingId,
			userId: userId ?? '',
		}),
	);
	return new BookingEntity(results.data[0]);
}

// Cancel event booking end-point
export async function cancelEventBooking(bookingInfo: BookingCancellationDto) {
	return axios.post(`${EVENT_BOOKING_URL}/cancel-event-booking`, bookingInfo, {
		validateStatus: () => true,
	});
}
